import { getB2BVariantInfoBySkus } from '@/shared/service/b2b';
import { productAvailabilityStatusMap } from './productAvailabilityStatusMap';
import { getProductBySku } from '@/shared/service/bc/graphql/product';

export const getVariantList = async (skus: string[]): Promise<any[]> => {
  try {
    const { variantSku: variantInfoList } = await getB2BVariantInfoBySkus({ skus }, true);
    return variantInfoList;
  } catch (error) {
    console.error('Error getting variant list:', error);
    return [];
  }
};

export interface ItemInfo {
  productUrl: string;
  quantity: number;
  name: string;
  price: number;
  imageUrl: string;
  confirmationStatus?: string;
  confirmedDeliveryDate?: string;
  cancellationStatusCode?: string;
}

export interface ProductInfo {
  availability: string;
  base_price: string;
  base_total: string;
  id: number;
  imageUrl: string;
  name: string;
  productUrl: string;
  product_id: number | string;
  quantity: number;
  sku: string;
  variantImageUrl: string;
  variant_id: number;
}

export interface DeliveryItem {
  productId: string;
  productName: string;
  confirmationStatusData: {
    translationKey: string;
    color: string;
  };
  confirmedDeliveryDate: string;
  confirmedQuantity: number;
}

export interface SAPOrderData {
  shippings: Array<{
    city: string;
    country: string;
    country_iso2: string;
    email: string;
    first_name: string;
    last_name: string;
    form_fields: any[];
    state: string;
    street_1: string;
    street_2: string;
    zip: string;
    notShip: { itemsInfo: ItemInfo[] };
    phone: string;
    shipmentItems: any[];
    shipping_method: string;
  }>;
  extraStatuses: Record<string, string>;
  billings: any[];
  history: any[];
  poNumber: string;
  status: string;
  canReturn: boolean;
  currency: string;
  currencyCode: string;
  customStatus: string;
  invoiceId: number;
  ipStatus: number;
  money: {
    currency_location: string;
    currency_token: string;
    decimal_places: number;
    decimal_token: string;
    thousands_token: string;
  };
  orderComments: string;
  orderId: number;
  orderIsDigital: boolean;
  orderSummary: {
    createAt: string;
    name: string;
    priceData: {
      subtotal: any;
      shipping: any;
      discountAmount: any;
      tax: any;
      grandTotal: any;
    };
    priceSymbol: {
      'Sub total': string;
      Shipping: string;
      'Handling Fee': string;
      Tax: string;
      'Discount amount': string;
    };
  };
  payment: {
    billingAddress: {
      zip: string;
      city: string;
      company: string;
      country: string;
      country_iso2: string;
      email: string;
      first_name: string;
      last_name: string;
      phone: string;
      state: string;
      street_1: string;
      street_2: string;
    };
    dateCreateAt: number;
    paymentMethod: string;
    updatedAt: string;
  };
  products: ProductInfo[];
  shippingInfo: {
    carrier?: string;
    carrierEmail?: string;
    carrierPhone?: string;
    deliveryAddress?: {
      recipientName?: string;
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
    };
    estimatedDeliveryDate?: string;
    deliveryItems?: DeliveryItem[];
  };
}

function formatDate(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
}

/**
 * Maps the order data returned by the SAP service to
 * the format expected by the system.
 *
 * @param order - Object returned by the SAP service.
 * @returns Mapped data or null if the order is invalid.
 */
export const mapSAPOrderData = async (order: any): Promise<SAPOrderData | null> => {
  console.log('found order:', order);
  if (!order) return null;

  const saleOrders = order['soap-env:Body']['n0:SalesOrderByElementsResponse_sync'].SalesOrder;

  // Extracting order information
  const firstName = saleOrders?.AccountParty?.Address?.Name?.Name?.FirstLineName || '';
  const streetName = saleOrders?.AccountParty?.Address?.PostalAddress?.StreetName || '';
  const city = saleOrders?.AccountParty?.Address?.PostalAddress?.CityName || '';
  const zip = saleOrders?.AccountParty?.Address?.PostalAddress?.StreetPostalCode || '';
  const countryCode = saleOrders?.AccountParty?.Address?.PostalAddress?.CountryCode || '';
  const orderDate = saleOrders?.PostingDate;
  const orderDateTimestamp = Math.floor(new Date(orderDate).getTime() / 1000);

  const statusFields = {
    itemListCustomerOrderLifeCycleStatusName: saleOrders?.Status?.ItemListCustomerOrderLifeCycleStatusName?.["#"],
    confirmationIssuingStatusName: saleOrders?.Status?.ConfirmationIssuingStatusName?.["#"],
  };


  const shippingInfo = {
      carrier: saleOrders?.FreightForwarderParty?.Address?.Name?.Name?.SecondLineName || '',
      carrierEmail: saleOrders?.FreightForwarderParty?.Address?.Email?.URI || '',
      carrierPhone: saleOrders?.FreightForwarderParty?.Address?.Telephone?.FormattedNumberDescription || '',
      deliveryAddress: {
          recipientName: saleOrders?.ProductRecipientParty?.Address?.Name?.Name?.FirstLineName || '',
          street: saleOrders?.ProductRecipientParty?.Address?.PostalAddress?.StreetName || '',
          city: saleOrders?.ProductRecipientParty?.Address?.PostalAddress?.CityName || '',
          postalCode: saleOrders?.ProductRecipientParty?.Address?.PostalAddress?.StreetPostalCode || '',
          country: saleOrders?.ProductRecipientParty?.Address?.PostalAddress?.CountryCode || ''
      },
      estimatedDeliveryDate: saleOrders?.RequestedFulfillmentPeriodPeriodTerms?.EndDateTime?.["#"] || '',

      deliveryItems: [] as DeliveryItem[]

  };


  const subtotal = saleOrders?.PriceAndTaxCalculation?.MainPrice?.CalculatedAmount['#'] ?? 0;

  let taxPrice = 0;
  if (saleOrders?.PriceAndTaxCalculation?.ProductTaxDetails) {
    const productTaxDetailsArray = Array.isArray(saleOrders.PriceAndTaxCalculation.ProductTaxDetails)
      ? saleOrders.PriceAndTaxCalculation.ProductTaxDetails
      : [saleOrders.PriceAndTaxCalculation.ProductTaxDetails];

    taxPrice = productTaxDetailsArray.reduce((sum: number, taxDetail: any) => {
      const amount = parseFloat(taxDetail?.ProductTax?.Amount?.['#']) || 0;
      return sum + amount;
    }, 0);
  }

  let shippingFee = 0;
  const priceComponents = saleOrders?.PriceAndTaxCalculation?.PriceComponent || [];

  const shippingComponent = priceComponents.find(
    (component: any) =>
      component.Description?.['#'] === 'Shipping' ||
      component.Description?.['#'] === 'Freight' ||     //TODO - check for corrects fields
      component.TypeCode?.['#'] === 'ZFRE'
  );

  if (shippingComponent) {
    shippingFee = parseFloat(shippingComponent.CalculatedAmount?.['#']) || 0;
  }

  const totalComponent = priceComponents.find(
    (component: any) => component.Description?.['#'] === 'Total',
  );

  const discountComponent = priceComponents.find(
    (component: any) => component.Description['#'] === 'Product Discount (%)',
  );
  const totalValue = totalComponent ? totalComponent.CalculatedAmount['#'] : null;
  const productDiscount = discountComponent
    ? Math.abs(discountComponent.CalculatedAmount['#'])
    : null;
  const accountEmail = saleOrders?.AccountParty?.ContactParty?.Address?.Email?.URI || '';
  const orderStatus = saleOrders?.Status?.ItemListCustomerOrderLifeCycleStatusName['#'] || '';

  const itemsInfo: ItemInfo[] = [];
  const productsInfo: ProductInfo[] = [];

  const deliveryItems: DeliveryItem[] = [];


  // Helper function to map a single item
const mapItem = async (item: any) => {
  try {
    const productName = item?.Description?.['#'] ?? '[Unknown Product]';
    const productPrice = parseFloat(item?.PriceAndTaxCalculationItem?.ItemMainPrice?.Rate?.DecimalValue ?? '0');

    const quantityStr = item?.PriceAndTaxCalculationItem?.ItemMainPrice?.CalculationBasis?.Quantity?.['#'];
    const productQuantity = quantityStr ? parseInt(quantityStr, 10) : 0;

    const productId = item?.ItemProduct?.ProductInternalID ?? null;
    if (!productId) {
      console.warn('[mapItem] Missing ProductInternalID in item:', item);
      return;
    }

    let bcProduct;
    try {
      bcProduct = await getProductBySku(productId);
    } catch (err) {
      console.error(`[mapItem] Failed to fetch product by SKU: ${productId}`, err);
      return;
    }

    const bcProductData = bcProduct?.data?.site?.product || {};
    const bcProductImage = bcProductData?.defaultImage?.urlOriginal ?? '';
    const bcProductId = bcProductData?.entityId ?? '';
    const bcProductSku = bcProductData?.sku ?? '';
    const bcProductName = bcProductData?.name ?? '';
    const bcProductPrice = bcProductData?.prices?.price?.value ?? '';
    const productUrl = bcProductData?.path ?? '';

    const confirmationStatusCode = item?.Status?.ProductAvailabilityConfirmationStatusCode ?? '';
    const confirmationStatusData = productAvailabilityStatusMap?.[confirmationStatusCode] ?? null;

    const cancellationStatusCode = item?.Status?.CancellationStatusCode ?? '';

    const itemScheduleLine = item?.ItemScheduleLine;
    const productAvailabilityStatus = item?.ProductAvailabilityConfirmationStatusCode;

    const confirmedScheduleLine = (
      Array.isArray(itemScheduleLine)
        ? itemScheduleLine
        : itemScheduleLine ? [itemScheduleLine] : []
    ).find((line: any) => line?.TypeCode === "2");

    const confirmedQuantity = parseInt(confirmedScheduleLine?.Quantity?.['#'] ?? '0', 10);

    const confirmedDeliveryDateRaw =
      productAvailabilityStatus !== "2" && confirmedQuantity > 0
        ? confirmedScheduleLine?.DateTimePeriod?.StartDateTime?.['#']
        : '';

    const confirmedDeliveryDate = formatDate(confirmedDeliveryDateRaw);

    deliveryItems.push({
      productId,
      productName,
      confirmationStatusData,
      confirmedDeliveryDate,
      confirmedQuantity,
    });

    itemsInfo.push({
      productUrl,
      quantity: productQuantity,
      name: productName,
      price: productPrice,
      imageUrl: bcProductImage,
      cancellationStatusCode,
    });

    if (bcProductSku) {
      productsInfo.push({
        availability: 'available',
        base_price: Number(bcProductPrice).toFixed(4),
        base_total: (productPrice * productQuantity).toFixed(4),
        id: Number(bcProductId),
        imageUrl: bcProductImage,
        name: bcProductName,
        productUrl,
        product_id: bcProductId,
        quantity: productQuantity,
        sku: bcProductSku,
        variantImageUrl: bcProductImage,
        variant_id: Number(bcProductId),
      });
    }
  } catch (error) {
    console.error('[mapItem] Unexpected error mapping item:', item, error);
  }
};



  shippingInfo.deliveryItems = deliveryItems;

  // Maps all items, considering whether it is an array or a single object
  if (Array.isArray(saleOrders.Item)) {
    await Promise.all(saleOrders.Item.map((item: any) => mapItem(item)));
  } else {
    await mapItem(saleOrders.Item);
  }

  const filteredStatus = Object.fromEntries(
    Object.entries(statusFields).filter(([_, value]) => value !== undefined && value !== '')
  );



  

  const data: SAPOrderData = {
    ...filteredStatus,
    shippingInfo,
    shippings: [
      {
        city,
        country: countryCode,
        country_iso2: countryCode,
        email: accountEmail,
        first_name: firstName,
        last_name: ' ',
        form_fields: [],
        state: '',
        street_1: streetName,
        street_2: '',
        zip,
        notShip: { itemsInfo },
        phone: '',
        shipmentItems: [],
        shipping_method: 'None',
      },
    ],
    extraStatuses: {},
    billings: [],
    history: [],
    poNumber: '',
    status: orderStatus,
    canReturn: false,
    currency: '€',
    currencyCode: 'EUR',
    customStatus: orderStatus,
    invoiceId: 0,
    ipStatus: 0,
    money: {
      currency_location: 'left',
      currency_token: '€',
      decimal_places: 2,
      decimal_token: ',',
      thousands_token: ' ',
    },
    orderComments: '',
    orderId: 110,
    orderIsDigital: false,
    orderSummary: {
      createAt: orderDate,
      name: firstName,
      priceData: {
        subtotal: subtotal,
        shipping: shippingFee,
        discountAmount: productDiscount,
        tax: taxPrice,
        grandTotal: totalValue,
      },
      priceSymbol: {
        'Sub total': 'subTotal',
        Shipping: 'shipping',
        'Handling Fee': 'handingFee',
        Tax: 'tax',
        'Discount amount': 'discountAmount',
      },
    },
    payment: {
      billingAddress: {
        zip,
        city,
        company: '',
        country: countryCode,
        country_iso2: countryCode,
        email: accountEmail,
        first_name: firstName,
        last_name: ' ',
        phone: '',
        state: ' ',
        street_1: streetName,
        street_2: '',
      },
      dateCreateAt: orderDateTimestamp,
      paymentMethod: '',
      updatedAt: '',
    },
    products: productsInfo,
  };


  return data;
};
